/**
 * usePrinterSelect
 *
 * Bundles `usePrinterDiscovery` with a `selectedPrinter` ref and an
 * auto-select-first helper so each print dialog doesn't need to
 * duplicate the same destructure → ref → watch → discover → select pattern.
 *
 * Usage:
 *   const {
 *     selectedPrinter, printerOptions, isDiscovering,
 *     discoverError, discoverAndSelect,
 *   } = usePrinterSelect();
 *
 *   // In a visibility watcher:
 *   await discoverAndSelect();          // resets selection, discovers, picks first
 *   await discoverAndSelect(true);      // force re-discover (bypass cache)
 */
import { ref } from 'vue';
import { usePrinterDiscovery } from './usePrinterDiscovery';
import type { PrinterOption } from './usePrinterDiscovery';
import { API_ROUTES } from '@/core/config/api-routes';
import type { UserLocationAssignment } from '@tms-phoenix/tms-types';

type AuthSnapshot = {
  staffId?: unknown;
  userDetails?: {
    sub?: unknown;
    care_unit_id?: unknown;
  };
};

function readAuthSnapshot(): AuthSnapshot | null {
  try {
    const raw = localStorage.getItem('pinia-auth');
    if (!raw) return null;
    return JSON.parse(raw) as AuthSnapshot;
  } catch {
    return null;
  }
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

async function resolveCurrentUserCareUnitId(): Promise<string | null> {
  const snapshot = readAuthSnapshot();

  const userId = toStringOrNull(snapshot?.staffId)
    ?? toStringOrNull(snapshot?.userDetails?.sub)
    ?? null;

  if (userId) {
    try {
      const { getApiClient } = await import('@/core/services/api.service');
      const api = getApiClient();
      // The API client unwraps { success, data, meta } → response.data is UserLocationAssignment.
      const response = await api.get<UserLocationAssignment>(
        `${API_ROUTES.ONBOARDING}/user-location-assignments/${userId}`,
      );
      const unitId = response.data.unit_id || null;
      if (unitId) return unitId;
    } catch {
      // Ignore assignment lookup failures and fall back to local snapshot.
    }
  }

  return toStringOrNull(snapshot?.userDetails?.care_unit_id) ?? null;
}

export function usePrinterSelect() {
  const {
    printers,
    printerOptions,
    isDiscovering,
    discoverError,
    discover,
  } = usePrinterDiscovery();

  /** Currently selected printer — bound via `v-model` on `<PrinterSelect>`. */
  const selectedPrinter = ref<PrinterOption | null>(null);

  /**
   * Reset the selection, run printer discovery, then auto-select:
   *   1. The printer whose `care_unit_id` matches the logged-in user's assigned unit.
   *   2. If no unit match, fall back to the first available printer.
   *
   * @param force  When `true` bypasses the per-hospital cache (passed through
   *               to `usePrinterDiscovery.discover()`).
   */
  async function discoverAndSelect(force = false): Promise<void> {
    selectedPrinter.value = null;
    await discover(force);
    if (printerOptions.value.length === 0) return;

    // 1. Try to match the printer assigned to the user's care unit.
    // Resolve against assignment API first (fresh mapping), then fall back
    // to auth snapshot if the assignment endpoint is unavailable.
    const userCareUnitId = await resolveCurrentUserCareUnitId();
    if (userCareUnitId) {
      const unitPrinter = printers.value.find((p) => p.careUnitId === userCareUnitId);
      if (unitPrinter) {
        selectedPrinter.value =
          printerOptions.value.find((o) => o.value === unitPrinter.deviceId) ?? printerOptions.value[0];
        return;
      }
    }

    // 2. Fall back to first available printer
    selectedPrinter.value = printerOptions.value[0];
  }

  return {
    /** Reactive selected printer — use with `v-model` on `<PrinterSelect>`. */
    selectedPrinter,
    /** Dropdown-friendly options list. */
    printerOptions,
    /** Whether a discovery request is currently in-flight. */
    isDiscovering,
    /** Last discovery error message, if any. */
    discoverError,
    /** Discover printers and auto-select the first result. */
    discoverAndSelect,
  };
}
